import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/Select";
import ScenathonCharts from "../components/Scenathon/ScenathonCharts";
import CountriesWithSubmissionStatus from "../components/CountriesWithSubmissionStatus";
import ComparisonScenathon from "./ComparisonScenathon";

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = "scenathon_send_results_page_v5";

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) {
  return (
    <Select
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full"
    />
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 hover:border-[#54B046] p-5 shadow-sm hover:shadow-md transition-all group">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-600 group-hover:text-[#54B046] transition-colors">
        {label}
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900 group-hover:text-[#54B046] transition-colors">
        {value || "—"}
      </div>
    </div>
  );
}

// Parse tabular data from Excel (TSV format)
function parseTableData(rawText) {
  if (!rawText || typeof rawText !== "string") return [];
  
  const lines = rawText
    .trim()
    .split(/[\n\r]+/)
    .filter((line) => line.trim());
  
  if (lines.length < 2) return []; // Need at least header + 1 data row

  // First line is header
  const headerLine = lines[0];
  let headers = headerLine.split(/\t/).map((h) => h.trim());

  // Normalize header names: map "Location" to "country"
  headers = headers.map((h) => (h === "Location" ? "country" : h));

  // Parse data rows (all lines after header)
  const rows = lines.slice(1).map((line) => {
    const values = line.split(/\t/).map((v) => v.trim());
    const row = {};
    headers.forEach((header, i) => {
      // Preserve "0" as a value, only convert empty strings to null
      row[header] = (values[i] === undefined || values[i] === "") ? null : values[i];
    });
    return row;
  });

  console.log(`[parseTableData] Total lines: ${lines.length}, Headers: ${headers.length}, Rows parsed: ${rows.length}`);
  console.log(`[parseTableData] First row sample:`, rows[0]);

  return rows;
}

// Submission form component
function SubmissionForm({ scenathonId, scenathonOptions, onScenathonChange, apiFetch, API, defaultPathways, loadingDefaultPathways, selectedDefaultPathwayId, onDefaultPathwayChange, isAdmin, isTeamleader, user }) {
  const { accessToken, csrfToken } = useAuth(); // Get auth tokens
  const [indicatorsData, setIndicatorsData] = useState("");
  const [tradeData, setTradeData] = useState("");
  const [pathwaysData, setPathwaysData] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState(null);
  const [tradeAdjustment, setTradeAdjustment] = useState("");
  const [participatingCountries, setParticipatingCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Upload Initial Data states
  const [excelFile, setExcelFile] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  // Check pathway exists state
  const [pathwayExists, setPathwayExists] = useState(false);
  const [checkingPathway, setCheckingPathway] = useState(false);

  // Submission history state
  const [submissionHistory, setSubmissionHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showIndicatorsModal, setShowIndicatorsModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [showPathwaysModal, setShowPathwaysModal] = useState(false);

  // Determine if user can change scenathon/country (only admins can)
  const isReadOnlyUser = !isAdmin && !isTeamleader && user;
  const userScenathonId = user?.scenathon_id;
  const userCountry = user?.country;

  // Initialize selectedCountry: start empty, will be set when countries load
  const [selectedCountry, setSelectedCountry] = useState("");

  const parsedIndicators = useMemo(() => parseTableData(indicatorsData), [indicatorsData]);
  const parsedTrade = useMemo(() => parseTableData(tradeData), [tradeData]);
  const parsedPathways = useMemo(() => parseTableData(pathwaysData), [pathwaysData]);

  // Load participating countries from DB when scenathonId changes
  useEffect(() => {
    if (!scenathonId) {
      setParticipatingCountries([]);
      setSelectedCountry("");
      return;
    }

    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await apiFetch(`${API}/api/scenathon/scenathons/${scenathonId}/participating-countries`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.countries)) {
            setParticipatingCountries(data.countries);
            
            console.log("participatingCountries:", data.countries);
            console.log("userCountry:", userCountry);
            console.log("isAdmin:", isAdmin);
            
            // For non-admin users (including teamleader), auto-select their assigned country (if it exists in this scenathon)
            // For admin, auto-select first country from list
            if (!isAdmin && userCountry) {
              // Non-admin: check if userCountry is in this scenathon's participating countries
              const userCountryExists = data.countries.find(c => c.country_code === userCountry);
              if (userCountryExists) {
                setSelectedCountry(userCountry);
                console.log("Setting non-admin country to:", userCountry);
              } else {
                // User's country not in this scenathon, use first available
                if (data.countries.length > 0) {
                  setSelectedCountry(data.countries[0].country_code);
                  console.warn("User country", userCountry, "not in scenathon. Using", data.countries[0].country_code);
                }
              }
            } else if (isAdmin && data.countries.length > 0) {
              // Admin: first country
              setSelectedCountry(data.countries[0].country_code);
              console.log("Setting admin country to:", data.countries[0].country_code);
            }
          }
        }
      } catch (err) {
        console.error("Error loading participating countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [scenathonId, isAdmin, userCountry, apiFetch, API]);

  // For non-admin users (including teamleader), force selectedCountry to always be userCountry
  useEffect(() => {
    if (!isAdmin && userCountry && selectedCountry !== userCountry) {
      console.log("Forcing selectedCountry to:", userCountry);
      setSelectedCountry(userCountry);
    }
  }, [isAdmin, userCountry]);

  // Check if pathway submission already exists
  useEffect(() => {
    if (!scenathonId || !selectedCountry || !selectedDefaultPathwayId) {
      setPathwayExists(false);
      return;
    }

    let alive = true;

    const checkPathwayExists = async () => {
      setCheckingPathway(true);
      try {
        const params = new URLSearchParams({
          scenathonId: Number(scenathonId),
          defaultPathwayId: Number(selectedDefaultPathwayId),
          country: selectedCountry,
        });

        const res = await apiFetch(
          `${API}/api/scenathon/check-pathway-exists?${params.toString()}`,
        );

        if (!alive) return;

        if (res.ok) {
          const data = await res.json();
          setPathwayExists(data.exists || false);
        } else {
          setPathwayExists(false);
        }
      } catch (err) {
        console.error("Error checking pathway existence:", err);
        if (alive) setPathwayExists(false);
      } finally {
        if (alive) setCheckingPathway(false);
      }
    };

    checkPathwayExists();

    return () => {
      alive = false;
    };
  }, [scenathonId, selectedCountry, selectedDefaultPathwayId, apiFetch, API]);

  // Load submission history
  useEffect(() => {
    if (!scenathonId || !selectedCountry || !selectedDefaultPathwayId) {
      setSubmissionHistory(null);
      return;
    }

    let alive = true;

    const loadHistory = async () => {
      setLoadingHistory(true);
      try {
        const params = new URLSearchParams({
          scenathonId: Number(scenathonId),
          defaultPathwayId: Number(selectedDefaultPathwayId),
          country: selectedCountry,
          tradeAdjustment: tradeAdjustment || "",
        });

        const res = await apiFetch(
          `${API}/api/scenathon/get-submission-history?${params.toString()}`,
        );

        if (!alive) return;

        if (res.ok) {
          const data = await res.json();
          setSubmissionHistory(data);
        } else {
          setSubmissionHistory(null);
        }
      } catch (err) {
        console.error("Error loading submission history:", err);
        if (alive) setSubmissionHistory(null);
      } finally {
        if (alive) setLoadingHistory(false);
      }
    };

    loadHistory();

    return () => {
      alive = false;
    };
  }, [scenathonId, selectedCountry, selectedDefaultPathwayId, tradeAdjustment, apiFetch, API]);

  const canSubmit = Boolean(
    scenathonId && parsedIndicators.length > 0 && parsedTrade.length > 0 && selectedDefaultPathwayId && tradeAdjustment && selectedCountry && (tradeAdjustment ? parsedPathways.length > 0 : true)
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      // Check if pathway data already exists for this combination
      const checkParams = new URLSearchParams({
        scenathonId: Number(scenathonId),
        defaultPathwayId: Number(selectedDefaultPathwayId),
        country: selectedCountry,
      });

      const checkRes = await apiFetch(
        `${API}/api/scenathon/check-pathway-exists?${checkParams.toString()}`,
      );

      let pathwayExists = false;
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        pathwayExists = checkData.exists || false;
      }

      // If pathway exists, ask for confirmation
      if (pathwayExists) {
        const result = await Swal.fire({
          icon: "warning",
          title: "Pathway Already Exists",
          text: `Data for this pathway and country combination already exists. Do you want to replace it?`,
          showCancelButton: true,
          confirmButtonText: "Yes, Replace",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#d32f2f",
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      setSubmitting(true);

      // Filter data by country with normalized comparison
      const filteredIndicators = parsedIndicators.filter(row => {
        const rowCountry = String(row.country || "").trim().toUpperCase();
        const selected = String(selectedCountry || "").trim().toUpperCase();
        return rowCountry === selected;
      });
      
      const filteredTrade = parsedTrade.filter(row => {
        const rowCountry = String(row.country || "").trim().toUpperCase();
        const selected = String(selectedCountry || "").trim().toUpperCase();
        return rowCountry === selected;
      });

      // Debug logging
      console.log("[handleSubmit] selectedCountry:", selectedCountry);
      console.log("[handleSubmit] parsedIndicators count:", parsedIndicators.length);
      console.log("[handleSubmit] parsedTrade count:", parsedTrade.length);
      console.log("[handleSubmit] parsedIndicators first 3:", parsedIndicators.slice(0, 3));
      console.log("[handleSubmit] parsedTrade first 3:", parsedTrade.slice(0, 3));
      console.log("[handleSubmit] filteredIndicators count:", filteredIndicators.length);
      console.log("[handleSubmit] filteredTrade count:", filteredTrade.length);

      // Check if filtering resulted in empty arrays
      if (filteredIndicators.length === 0 || filteredTrade.length === 0) {
        const headers = parsedIndicators[0] ? Object.keys(parsedIndicators[0]) : [];
        const sampleCountries = parsedIndicators.slice(0, 5).map(r => r.country);
        Swal.fire({
          icon: "error",
          title: "No matching data",
          html: `<div style="text-align: left; font-size: 14px;">
            <p><strong>Selected country:</strong> ${selectedCountry}</p>
            <p><strong>Available columns:</strong> ${headers.join(", ")}</p>
            <p><strong>Sample country values from data:</strong> ${sampleCountries.join(", ")}</p>
            <p><strong>Indicators rows matching:</strong> ${filteredIndicators.length}</p>
            <p><strong>Trade rows matching:</strong> ${filteredTrade.length}</p>
            <p style="margin-top: 10px; color: #d32f2f;"><strong>Issue:</strong> No rows found with "country" column matching "${selectedCountry}"</p>
            <p style="margin-top: 10px; font-size: 12px; color: #666;">Make sure the pasted data includes a "country" column with values matching the selected country code.</p>
          </div>`,
        });
        setSubmitting(false);
        return;
      }

      const res = await apiFetch(`${API}/api/scenathon/bulk-insert`, {
        method: "POST",
        body: JSON.stringify({
          scenathonId: Number(scenathonId),
          defaultPathwayId: Number(selectedDefaultPathwayId),
          tradeAdjustment: tradeAdjustment === "yes",
          country: selectedCountry,
          indicatorsData: filteredIndicators,
          tradeData: filteredTrade,
          pathwaysData: pathwaysData || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const data = await res.json();

      // Save tradeAdjustment for use in pathway naming
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        stored.tradeAdjustment = tradeAdjustment;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (e) {
        // ignore storage errors
      }

      Swal.fire({
        icon: "success",
        title: "Data submitted successfully",
        text: `Inserted ${data.indicatorsReportCount} indicator report rows, ${data.indicatorsScenathonCount} indicator scenathon rows, ${data.tradeReportCount} trade report rows, and ${data.pathwaysCount || 0} pathways selection record(s).`,
      });

      setIndicatorsData("");
      setTradeData("");
      setPathwaysData("");
      setTradeAdjustment("");
      onDefaultPathwayChange("");
    } catch (err) {
      console.error(err);
      
      // Check if this is an S12 validation error
      const errorMessage = err.message || "An error occurred submitting the data.";
      const isS12ValidationError = errorMessage.includes("Column S12");
      
      if (isS12ValidationError) {
        // Extract S12 value and Trade Adjustment from error message
        const s12ValueMatch = errorMessage.match(/Column S12 value "([^"]+)"/);
        const tradeAdjMatch = errorMessage.match(/Trade Adjustment selection "([^"]+)"/);
        const s12Value = s12ValueMatch ? s12ValueMatch[1] : "Unknown";
        const tradeAdjValue = tradeAdjMatch ? tradeAdjMatch[1] : "Unknown";
        
        Swal.fire({
          icon: "error",
          title: "Validation Error: S12 Mismatch",
          html: `<div style="text-align: left; font-size: 14px;">
            <p><strong>Column S12 (Trade adjustment to ensure global trade balance):</strong></p>
            <p style="margin: 10px 0; color: #1976d2; font-weight: bold;">Value in Excel: "${s12Value}"</p>
            <p><strong>Trade Adjustment selection:</strong></p>
            <p style="margin: 10px 0; color: #1976d2; font-weight: bold;">Value selected: "${tradeAdjValue}"</p>
            <p style="margin-top: 15px; color: #d32f2f;"><strong>Issue:</strong> The values do not match.</p>
            <p style="margin-top: 10px; font-size: 12px; color: #666;">Please go to the Trade Adjustment select field and choose <strong>"${s12Value.toLowerCase()}"</strong> to match the Excel data, then submit again.</p>
          </div>`,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Could not submit data",
          text: errorMessage,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Excel file upload for "Upload Initial Data"
  const handleExcelUpload = useCallback(async () => {
    if (!excelFile) {
      Swal.fire({
        icon: "warning",
        title: "No file selected",
        text: "Please select an Excel file to upload.",
      });
      return;
    }

    if (!scenathonId) {
      Swal.fire({
        icon: "warning",
        title: "Scenathon required",
        text: "Please select a scenathon first.",
      });
      return;
    }

    if (!selectedCountry) {
      Swal.fire({
        icon: "warning",
        title: "Country required",
        text: "Please select a country first.",
      });
      return;
    }

    try {
      setUploadingExcel(true);
      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("scenathonId", scenathonId);
      formData.append("country", selectedCountry);
      formData.append("defaultPathwayId", selectedDefaultPathwayId || "");
      formData.append("tradeAdjustment", tradeAdjustment || "");

      // Use fetch directly for FormData (apiFetch sets Content-Type: application/json)
      const headers = {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      };

      const res = await fetch(`${API}/api/scenathon/upload-excel`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const data = await res.json();

      if (data.success && data.extractedData) {
        setIndicatorsData(data.extractedData.indicatorsData);
        setTradeData(data.extractedData.tradeData);
        if (data.extractedData.pathwaysData) {
          setPathwaysData(data.extractedData.pathwaysData);
        }
        setExcelFile(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";

        const msg = `Indicators: ${data.data.indicatorsDataRows} rows | Trade: ${data.data.tradeDataRows} rows${data.data.pathwaysDataRows ? ` | Pathways: ${data.data.pathwaysDataRows} rows` : ""}`;
        Swal.fire({
          icon: "success",
          title: "Data extracted successfully",
          text: msg,
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Could not extract data",
        text: err.message || "An error occurred uploading the file.",
      });
    } finally {
      setUploadingExcel(false);
    }
  }, [excelFile, scenathonId, selectedCountry, selectedDefaultPathwayId, tradeAdjustment, API, accessToken, csrfToken]);

  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-1 h-8 bg-gradient-to-b from-[#54B046] to-emerald-500 rounded-full"></div>
          <h2 className="text-3xl font-bold text-slate-900">
            Submit Scenathon Data
          </h2>
        </div>
        <p className="mt-3 text-slate-600 ml-4">
          Copy and paste the data from the SCENATHON_report Worksheet in your FABLE-C spreadsheet.
        </p>
      </div>

      <div className="mb-8 grid gap-6 lg:grid-cols-4">
        {/* Scenathon: Admin can choose, Teamleader sees their own, Regular users see read-only */}
        {isAdmin && scenathonOptions.length > 0 && (
          <div className="space-y-2">
            <SelectField
              label="Scenathon"
              value={scenathonId || ""}
              onChange={onScenathonChange}
              options={scenathonOptions}
              placeholder="Select scenathon"
              disabled={false}
            />
          </div>
        )}

        {isTeamleader && scenathonOptions.length > 0 && (
          <div className="space-y-2">
            <SelectField
              label="Scenathon"
              value={scenathonId || ""}
              onChange={onScenathonChange}
              options={scenathonOptions}
              placeholder="Select scenathon"
              disabled={true}
            />
          </div>
        )}

        {isReadOnlyUser && (
          <div className="space-y-2">
            <label className="text-sm font-bold uppercase tracking-wider text-slate-700">Scenathon</label>
            <div className="mt-2 rounded-lg border border-slate-300 bg-gradient-to-br from-slate-50 to-slate-100 p-4 text-sm font-semibold text-slate-900 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-[#54B046] rounded-full"></span>
              {scenathonOptions.find(opt => String(opt.value) === String(scenathonId))?.label || scenathonId || "—"}
            </div>
          </div>
        )}

        {scenathonId && (
          <>
            <div className="space-y-2">
              <SelectField
                label="Country"
                value={selectedCountry}
                onChange={isAdmin ? setSelectedCountry : undefined}
                options={isAdmin ? participatingCountries.map(item => ({
                  value: item.country_code,
                  label: item.country_code
                })) : (userCountry ? [{ value: userCountry, label: userCountry }] : [])}
                placeholder="Select country"
                disabled={loadingCountries || participatingCountries.length === 0 || !isAdmin}
              />
            </div>

            <div className="space-y-2">
              <SelectField
                label="Pathway Default Name"
                value={selectedDefaultPathwayId || ""}
                onChange={onDefaultPathwayChange}
                options={[
                  { value: "", label: "Select pathway..." },
                  ...defaultPathways.map((pw) => ({
                    value: String(pw.id),
                    label: pw.defaultname || "(unnamed)",
                  })),
                ]}
                placeholder="Select pathway"
                disabled={loadingDefaultPathways}
              />
            </div>

            <div className="space-y-2">
              <SelectField
                label="Trade Adjustment"
                value={tradeAdjustment}
                onChange={setTradeAdjustment}
                options={[
                  { value: "", label: "Select option..." },
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                placeholder="Select option"
              />
            </div>
          </>
        )}
      </div>

      {/* Upload Initial Data Section */}
      {scenathonId && (
        <div className="mb-8 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full"></div>
            <h3 className="text-lg font-bold text-slate-900">Extract data from FABLE-C</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Select an Excel file (.xlsx) with the SCENATHON_report worksheet containing Reporting_Aggregate and Reporting_Byproduct tables, plus an optional PATHWAYS selection table.
          </p>
          
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
              disabled={uploadingExcel}
              className="flex-1 rounded-lg border-2 border-blue-300 bg-white px-4 py-2 text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-500 file:px-3 file:py-1 file:text-sm file:font-semibold file:text-white hover:file:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={handleExcelUpload}
              disabled={uploadingExcel || !excelFile}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 px-6 py-2.5 font-semibold text-white shadow-md hover:shadow-lg transition-all disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed"
            >
              {uploadingExcel ? (
                <>
                  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Extracting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3v-6" />
                  </svg>
                  Extract Data
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Indicators Data */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-base font-bold text-slate-900 uppercase tracking-wide">
              REPORTING_AGGREGATE
            </label>
            <div className="flex items-center gap-2">
              {parsedIndicators.length > 0 && (
                <span className="inline-block px-3 py-1 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 text-xs font-bold rounded-full">
                  {parsedIndicators.length} rows
                </span>
              )}
              {indicatorsData && (
                <button
                  type="button"
                  onClick={() => setIndicatorsData("")}
                  className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 px-3 py-1.5 text-xs font-semibold text-red-700 transition-all hover:shadow-md border border-red-200 group"
                  title="Clear all content"
                >
                  <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear
                </button>
              )}
            </div>
          </div>
          <textarea
            value={indicatorsData}
            onChange={(e) => setIndicatorsData(e.target.value)}
            placeholder="Paste here the data copied from the Reporting_aggregate table, located in SCENATHON_report worksheet, including the headers. From “Location” to “HistP”, up to the last row (e.g. A11:BX22). Check that the latest changes to your FABLE-C are implemented by saving the file before copying the data."
            className="w-full h-40 rounded-xl border-2 border-slate-200 hover:border-[#54B046] bg-white p-4 text-sm font-mono text-slate-900 outline-none transition-all focus:border-[#54B046] focus:bg-slate-50 focus:ring-4 focus:ring-emerald-100"
          />
          {parsedIndicators.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setPreviewType("indicators");
                setShowPreview(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 px-5 py-2.5 text-sm font-semibold text-blue-700 transition-all hover:shadow-md border border-blue-200 group"
            >
              <svg className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview Data
            </button>
          )}
        </div>

        {/* Trade Data */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-base font-bold text-slate-900 uppercase tracking-wide">
              REPORTING_BYPRODUCT
            </label>
            <div className="flex items-center gap-2">
              {parsedTrade.length > 0 && (
                <span className="inline-block px-3 py-1 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 text-xs font-bold rounded-full">
                  {parsedTrade.length} rows
                </span>
              )}
              {tradeData && (
                <button
                  type="button"
                  onClick={() => setTradeData("")}
                  className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 px-3 py-1.5 text-xs font-semibold text-red-700 transition-all hover:shadow-md border border-red-200 group"
                  title="Clear all content"
                >
                  <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear
                </button>
              )}
            </div>
          </div>
          <textarea
            value={tradeData}
            onChange={(e) => setTradeData(e.target.value)}
            placeholder="Paste here the data copied from the Reporting_byproduct table, located in SCENATHON_report worksheet, including the headers. From “Location” to “SOC”, up to the last row (e.g. A29:AI953). Check that the latest changes to your FABLE-C are implemented by saving the file before copying the data."
            className="w-full h-40 rounded-xl border-2 border-slate-200 hover:border-[#54B046] bg-white p-4 text-sm font-mono text-slate-900 outline-none transition-all focus:border-[#54B046] focus:bg-slate-50 focus:ring-4 focus:ring-emerald-100"
          />
          {parsedTrade.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setPreviewType("trade");
                setShowPreview(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 px-5 py-2.5 text-sm font-semibold text-blue-700 transition-all hover:shadow-md border border-blue-200 group"
            >
              <svg className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview Data
            </button>
          )}
        </div>
      </div>

      {/* Pathways Selection Data (Required when Trade Adjustment is selected) */}
      <div className="mt-8 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-base font-bold text-slate-900 uppercase tracking-wide">
            PATHWAYS SELECTION
          </label>
          <div className="flex items-center gap-2">
            {parsedPathways.length > 0 && (
              <span className="inline-block px-3 py-1 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 text-xs font-bold rounded-full">
                {parsedPathways.length} rows
              </span>
            )}
            {pathwaysData && (
              <button
                type="button"
                onClick={() => setPathwaysData("")}
                className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 px-3 py-1.5 text-xs font-semibold text-red-700 transition-all hover:shadow-md border border-red-200 group"
                title="Clear all content"
              >
                <svg className="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear
              </button>
            )}
          </div>
        </div>
        <textarea
          value={pathwaysData}
          onChange={(e) => setPathwaysData(e.target.value)}
          placeholder="Paste here the data copied from the PATHWAYS selection table. This table is extracted automatically from your Excel file if it exists."
          className="w-full h-20 rounded-xl border-2 border-slate-200 hover:border-[#54B046] bg-white p-4 text-sm font-mono text-slate-900 outline-none transition-all focus:border-[#54B046] focus:bg-slate-50 focus:ring-4 focus:ring-emerald-100"
        />
        {parsedPathways.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setPreviewType("pathways");
              setShowPreview(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 px-5 py-2.5 text-sm font-semibold text-blue-700 transition-all hover:shadow-md border border-blue-200 group"
          >
            <svg className="h-4 w-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview Data
          </button>
        )}
      </div>

      <div className="mt-8 flex gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2E7D32] to-emerald-600 hover:shadow-lg px-8 py-3 text-base font-bold text-white transition-all hover:scale-105 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:scale-100 disabled:shadow-none group"
        >
          <svg className={`h-5 w-5 ${submitting ? 'animate-spin' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {submitting ? "Submitting..." : "Submit Data"}
        </button>
      </div>

      {/* Submission History */}
      {scenathonId && selectedCountry && selectedDefaultPathwayId && (
        <div className="mt-12">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full"></div>
              <h3 className="text-lg font-bold text-slate-900">Submission History</h3>
              {loadingHistory ? (
                <svg className="w-4 h-4 animate-spin text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
            </div>
            <p className="text-sm text-slate-600 ml-4">
              View all submissions for this scenario
            </p>
          </div>

          {loadingHistory ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-600">
              Loading submission history...
            </div>
          ) : !submissionHistory || (submissionHistory.stats.indicatorsRows === 0 && submissionHistory.stats.tradeRows === 0) ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-600">
              No submissions yet for this scenario
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-slate-100 to-slate-200 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left font-bold text-slate-900">Scenathon</th>
                    <th className="px-6 py-4 text-left font-bold text-slate-900">Country</th>
                    <th className="px-6 py-4 text-left font-bold text-slate-900">Pathway Default Name</th>
                    <th className="px-6 py-4 text-left font-bold text-slate-900">Trade Adjustment</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-900">REPORTING_AGGREGATE</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-900">REPORTING_BYPRODUCT</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-900">PATHWAYS SELECTION</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-700 font-semibold">{submissionHistory.scenathonName || "—"}</td>
                    <td className="px-6 py-4 text-slate-700 font-semibold">{submissionHistory.country || "—"}</td>
                    <td className="px-6 py-4 text-slate-700 font-semibold">{submissionHistory.pathway || "—"}</td>
                    <td className="px-6 py-4 text-slate-700">
                      {submissionHistory.tradeAdjustment && submissionHistory.tradeAdjustment !== '' ? (
                        <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full border border-yellow-200">
                          {submissionHistory.tradeAdjustment === 'yes' ? 'Yes' : 'No'}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {submissionHistory.stats.indicatorsRows > 0 ? (
                        <button
                          type="button"
                          onClick={() => setShowIndicatorsModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-bold transition-all hover:shadow-md"
                        >
                          <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View ({submissionHistory.stats.indicatorsRows})
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {submissionHistory.stats.tradeRows > 0 ? (
                        <button
                          type="button"
                          onClick={() => setShowTradeModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white text-xs font-bold transition-all hover:shadow-md"
                        >
                          <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View ({submissionHistory.stats.tradeRows})
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {submissionHistory.pathwaysData ? (
                        <button
                          type="button"
                          onClick={() => setShowPathwaysModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-xs font-bold transition-all hover:shadow-md"
                        >
                          <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View ({submissionHistory.pathwaysData.row_count || 0})
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Indicators Modal */}
      {showIndicatorsModal && submissionHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 px-8 py-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-green-600 rounded-full"></div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">REPORTING_AGGREGATE</h3>
                  <p className="text-sm text-slate-600 mt-1">{submissionHistory.stats.indicatorsRows} rows</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowIndicatorsModal(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
              >
                <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gradient-to-r from-slate-100 to-slate-200 z-10 border-b border-slate-200">
                  <tr>
                    {submissionHistory.indicatorsData[0] &&
                      Object.keys(submissionHistory.indicatorsData[0]).map((key) => (
                        <th
                          key={key}
                          className="border-r border-slate-200 px-4 py-3 text-left font-bold text-slate-700 whitespace-nowrap"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {submissionHistory.indicatorsData.map((row, i) => (
                    <tr key={i} className={`border-b border-slate-200 hover:bg-emerald-50/40 transition-colors ${
                      i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                    }`}>
                      {Object.values(row).map((value, j) => (
                        <td key={j} className="border-r border-slate-200 px-4 py-2 text-slate-700">
                          {value !== null && value !== undefined ? String(value).substring(0, 50) : "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Trade Modal */}
      {showTradeModal && submissionHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-8 py-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-cyan-600 rounded-full"></div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">REPORTING_BYPRODUCT</h3>
                  <p className="text-sm text-slate-600 mt-1">{submissionHistory.stats.tradeRows} rows</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowTradeModal(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
              >
                <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gradient-to-r from-slate-100 to-slate-200 z-10 border-b border-slate-200">
                  <tr>
                    {submissionHistory.tradeData[0] &&
                      Object.keys(submissionHistory.tradeData[0]).map((key) => (
                        <th
                          key={key}
                          className="border-r border-slate-200 px-4 py-3 text-left font-bold text-slate-700 whitespace-nowrap"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {submissionHistory.tradeData.map((row, i) => (
                    <tr key={i} className={`border-b border-slate-200 hover:bg-blue-50/40 transition-colors ${
                      i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                    }`}>
                      {Object.values(row).map((value, j) => (
                        <td key={j} className="border-r border-slate-200 px-4 py-2 text-slate-700">
                          {value !== null && value !== undefined ? String(value).substring(0, 50) : "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pathways Modal */}
      {showPathwaysModal && submissionHistory && submissionHistory.pathwaysData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-6xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-8 py-6 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">PATHWAYS SELECTION</h3>
                  <p className="text-sm text-slate-600 mt-1">{submissionHistory.pathwaysData.row_count || 0} rows</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPathwaysModal(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
              >
                <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto flex-1">
              {submissionHistory.pathwaysData.data ? (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gradient-to-r from-slate-100 to-slate-200 z-10 border-b border-slate-200">
                    <tr>
                      {submissionHistory.pathwaysData.data.headers && submissionHistory.pathwaysData.data.headers.map((header, idx) => (
                        <th
                          key={idx}
                          className="border-r border-slate-200 px-4 py-3 text-left font-bold text-slate-700 whitespace-nowrap"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {submissionHistory.pathwaysData.data.preHeaderRow && (
                      <tr className="bg-yellow-50/50 border-b border-yellow-200">
                        {submissionHistory.pathwaysData.data.preHeaderRow.map((cell, idx) => (
                          <td key={idx} className="border-r border-slate-200 px-4 py-2 text-slate-600 italic">
                            {cell || "—"}
                          </td>
                        ))}
                      </tr>
                    )}
                    {submissionHistory.pathwaysData.data.rows && submissionHistory.pathwaysData.data.rows.map((row, i) => (
                      <tr key={i} className={`border-b border-slate-200 hover:bg-purple-50/40 transition-colors ${
                        i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                      }`}>
                        {row.map((cell, j) => (
                          <td key={j} className="border-r border-slate-200 px-4 py-2 text-slate-700">
                            {cell || "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-slate-600">
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="w-full max-w-6xl rounded-2xl bg-white p-8 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-gradient-to-b from-[#54B046] to-emerald-500 rounded-full"></div>
                <h3 className="text-2xl font-bold text-slate-900">
                  {previewType === "indicators" ? "REPORTING_AGGREGATE" : previewType === "trade" ? "REPORTING_BYPRODUCT" : "PATHWAYS SELECTION"} Preview
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-all"
              >
                <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto flex-1 rounded-xl border border-slate-200 bg-slate-50">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gradient-to-r from-slate-100 to-slate-200 z-10">
                  <tr>
                    {(previewType === "indicators" ? parsedIndicators[0] : previewType === "trade" ? parsedTrade[0] : parsedPathways[0]) &&
                      Object.keys(
                        previewType === "indicators"
                          ? parsedIndicators[0]
                          : previewType === "trade"
                          ? parsedTrade[0]
                          : parsedPathways[0]
                      ).map((key) => (
                        <th
                          key={key}
                          className="border border-slate-300 px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-700"
                        >
                          {key}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {(previewType === "indicators" 
                    ? parsedIndicators
                    : previewType === "trade"
                    ? parsedTrade
                    : parsedPathways).map(
                    (row, i) => (
                      <tr key={i} className={`border-b border-slate-200 hover:bg-blue-50/40 transition-colors ${
                        i % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                      }`}>
                        {Object.values(row).map((value, j) => (
                          <td
                            key={j}
                            className="border-r border-slate-200 px-4 py-2 text-xs text-slate-700"
                          >
                            {value !== null && value !== undefined ? value : "—"}
                          </td>
                        ))}
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function normalizeResultsPayload(payload) {
  const root = Array.isArray(payload) ? payload[0] : payload;
  if (!root || typeof root !== "object") return null;

  const pathwayKey = Object.keys(root)[0];
  if (!pathwayKey) return null;

  const node = root[pathwayKey];
  const chartsBlock = node?.charts?.[0] || {};

  return {
    pathwayKey,
    node,
    charts: chartsBlock,
  };
}

function normalizeCountryOptionLabel(option) {
  if (typeof option === "string") {
    return option.trim().replace(/\s+/g, " ");
  }

  if (!option || typeof option !== "object") return "";

  const rawLabel = option.label || option.value || "";
  return String(rawLabel).trim().replace(/\s+/g, " ");
}

function extractCountriesFromCharts(chartsBlock) {
  if (!chartsBlock || typeof chartsBlock !== "object") return [];

  const collected = Object.values(chartsBlock).flatMap((section) => {
    if (!section || typeof section !== "object") return [];
    if (!Array.isArray(section.countries)) return [];

    return section.countries
      .map(normalizeCountryOptionLabel)
      .filter(Boolean);
  });

  const uniqueCountries = [...new Set(collected)];
  const nonGlobalCountries = uniqueCountries.filter(
    (country) => country.toLowerCase() !== "global",
  );

  return nonGlobalCountries.length > 0 ? nonGlobalCountries : uniqueCountries;
}

function readStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStoredState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export default function ScenathonSendResultsPage({ initialTab = "dashboard" }) {
  const { accessToken, csrfToken, logout, user, selectedScenathon } = useAuth();

  const stored = useMemo(() => readStoredState(), []);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingDefaultPathways, setLoadingDefaultPathways] = useState(false);

  const [scenathons, setScenathons] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [dashboardPathways, setDashboardPathways] = useState([]);
  const [defaultPathways, setDefaultPathways] = useState([]);

  // envío
  const [sendScenathonId, setSendScenathonId] = useState(() => {
    const isAdminOrTeamleader = user?.type_id === 1 || user?.type_id === 3;
    if (isAdminOrTeamleader) {
      return selectedScenathon?.id || stored?.sendScenathonId || "";
    } else {
      // Non-admin users get their assigned scenathon
      return user?.scenathon_id || selectedScenathon?.id || stored?.sendScenathonId || "";
    }
  });
  const [selectedPathwayId, setSelectedPathwayId] = useState(
    stored?.selectedPathwayId || "",
  );
  const [selectedDefaultPathwayId, setSelectedDefaultPathwayId] = useState(
    stored?.selectedDefaultPathwayId || "",
  );

  // resultados
  const [resultsScenathonId, setResultsScenathonId] = useState(
    stored?.resultsScenathonId || selectedScenathon?.id || "",
  );
  const [resultsPathwayId, setResultsPathwayId] = useState(
    stored?.resultsPathwayId || "",
  );
  const [sectionTab, setSectionTab] = useState(stored?.sectionTab || initialTab);
  const [dashboardTab, setDashboardTab] = useState(stored?.dashboardTab || "results");

  const [resultsPayload, setResultsPayload] = useState(
    stored?.resultsPayload || null,
  );
  const [resultsError, setResultsError] = useState(stored?.resultsError || "");

  // 🌍 Países de trade_adjustment_submissions
  const [countriesFromSubmissions, setCountriesFromSubmissions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // 🌍 Países participantes del scenathon
  const [participatingCountries, setParticipatingCountries] = useState([]);
  const [loadingParticipatingCountries, setLoadingParticipatingCountries] = useState(false);

  // 🌍 Países con datos en indicators_scenathon y trade_scenathon
  const [countriesWithData, setCountriesWithData] = useState([]);
  const [loadingCountriesWithData, setLoadingCountriesWithData] = useState(false);

  const canSeeSubmission =
    user?.type_id !== 2 && user?.type_id !== 5 && user?.type_id !== 4;

  const apiFetch = useCallback(async (url, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(options.headers || {}),
    };

    const res = await fetch(url, {
      credentials: "include",
      ...options,
      headers,
    });

    if (res.status === 401) {
      logout?.();
      throw new Error("Session expired");
    }

    return res;
  }, [accessToken, csrfToken, logout]);

  // Ensure non-admin users maintain their assigned scenathon
  useEffect(() => {
    const isAdminOrTeamleader = user?.type_id === 1 || user?.type_id === 3;
    if (!isAdminOrTeamleader && user?.scenathon_id && sendScenathonId !== user.scenathon_id) {
      setSendScenathonId(user.scenathon_id);
    }
  }, [user]);

  useEffect(() => {
    writeStoredState({
      sendScenathonId: selectedScenathon?.id || sendScenathonId,
      selectedPathwayId,
      selectedDefaultPathwayId,
      resultsScenathonId,
      resultsPathwayId,
      resultsPayload,
      resultsError,
      sectionTab,
      dashboardTab,
    });
  }, [
    sendScenathonId,
    selectedPathwayId,
    selectedDefaultPathwayId,
    resultsScenathonId,
    resultsPathwayId,
    resultsPayload,
    resultsError,
    sectionTab,
    dashboardTab,
  ]);

  // Sync sectionTab with initialTab when route changes
  useEffect(() => {
    setSectionTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!canSeeSubmission && sectionTab === "submission") {
      setSectionTab("dashboard");
    }
  }, [canSeeSubmission, sectionTab]);

  useEffect(() => {
    let alive = true;

    async function loadPageData() {
      setLoading(true);
      try {
        const [scRes] = await Promise.all([
          apiFetch(`${API}/api/scenathon/scenathons`),
          // pathways endpoint removed - pathways table no longer exists
        ]);

        if (!scRes.ok) throw new Error(`Scenathons HTTP ${scRes.status}`);

        const scData = await scRes.json();

        if (!alive) return;

        setScenathons(Array.isArray(scData?.scenathons) ? scData.scenathons : []);
        setPathways([]); // pathways table no longer exists
      } catch (err) {
        console.error(err);
        if (!alive) return;

        Swal.fire({
          icon: "error",
          title: "Could not load the page",
          text: err.message || "Error loading scenathons.",
        });
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadPageData();

    return () => {
      alive = false;
    };
  }, []);

  // Cargar default pathways del scenathon seleccionado para envío
  useEffect(() => {
    let alive = true;

    async function loadDefaultPathways() {
      if (!sendScenathonId) {
        setDefaultPathways([]);
        setSelectedDefaultPathwayId("");
        return;
      }

      setLoadingDefaultPathways(true);
      setSelectedDefaultPathwayId("");

      try {
        const res = await apiFetch(
          `${API}/api/scenathon/default-pathways/${sendScenathonId}`,
        );

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || `HTTP ${res.status}`);
        }

        const data = await res.json();

        if (!alive) return;
        setDefaultPathways(Array.isArray(data?.defaultPathways) ? data.defaultPathways : []);
      } catch (err) {
        console.error("Error loading default pathways:", err);
        if (!alive) return;
        setDefaultPathways([]);
        Swal.fire({
          icon: "error",
          title: "Could not load default pathways",
          text: err.message || "Error loading default pathways.",
        });
      } finally {
        if (alive) setLoadingDefaultPathways(false);
      }
    }

    loadDefaultPathways();

    return () => {
      alive = false;
    };
  }, [sendScenathonId]);

  useEffect(() => {
    setResultsPayload(null);
    setResultsError("");
  }, [resultsScenathonId, resultsPathwayId]);

  useEffect(() => {
    if (selectedScenathon?.id && selectedScenathon?.id !== sendScenathonId) {
      setSendScenathonId(selectedScenathon.id);
    }
  }, [selectedScenathon?.id, sendScenathonId]);

  useEffect(() => {
    if (selectedScenathon?.id && selectedScenathon?.id !== resultsScenathonId) {
      setResultsScenathonId(selectedScenathon.id);
    }
  }, [selectedScenathon?.id, resultsScenathonId]);

  useEffect(() => {
    if (!resultsScenathonId || !resultsPathwayId) return;
    loadScenathonResults(resultsScenathonId, resultsPathwayId);
  }, [resultsScenathonId, resultsPathwayId]);

  const loadScenathonResults = async (scenathonId, pathwayId) => {
    setResultsPayload(null);
    setResultsError("");

    if (!scenathonId || !pathwayId) return;

    setLoadingResults(true);
    try {
      const params = new URLSearchParams({
        scenathonId: String(scenathonId),
        pathwayName: String(pathwayId),
      });

      const res = await apiFetch(
        `${API}/api/scenathon/results?${params.toString()}`,
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `Scenathon results HTTP ${res.status}`);
      }

      const data = await res.json();
      setResultsPayload(data);
      setResultsError("");
    } catch (err) {
      console.error(err);
      setResultsError(err.message || "Could not load scenathon results.");
    } finally {
      setLoadingResults(false);
    }
  };

  // Cargar pathways disponibles para dashboard cuando cambia el scenathon
  useEffect(() => {
    if (!resultsScenathonId) {
      setDashboardPathways([]);
      setResultsPathwayId("");
      return;
    }

    const loadDashboardPathways = async () => {
      try {
        const res = await apiFetch(
          `${API}/api/scenathon/available-pathways/${resultsScenathonId}`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const nextPathways = data.pathways || [];
        setDashboardPathways(nextPathways);
        setResultsPathwayId((prev) => (
          nextPathways.some((pathway) => String(pathway.value) === String(prev)) ? prev : ""
        ));
      } catch (err) {
        console.error("Error loading dashboard pathways:", err);
        setDashboardPathways([]);
      }
    };

    loadDashboardPathways();
  }, [resultsScenathonId]);

  // 🌍 Cargar países de trade_adjustment_submissions
  useEffect(() => {
    if (!resultsScenathonId || !resultsPathwayId) {
      setCountriesFromSubmissions([]);
      return;
    }

    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const params = new URLSearchParams({
          scenathonId: String(resultsScenathonId),
          pathwayName: String(resultsPathwayId),
        });

        const res = await apiFetch(
          `${API}/api/scenathon/trade-adjustment-countries?${params.toString()}`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setCountriesFromSubmissions(Array.isArray(data?.countries) ? data.countries : []);
      } catch (err) {
        console.error("Error loading countries:", err);
        setCountriesFromSubmissions([]);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [resultsScenathonId, resultsPathwayId]);

  // 🌍 Cargar países participantes del scenathon
  useEffect(() => {
    if (!resultsScenathonId) {
      setParticipatingCountries([]);
      return;
    }

    const loadParticipatingCountries = async () => {
      setLoadingParticipatingCountries(true);
      try {
        const res = await apiFetch(
          `${API}/api/scenathon/scenathons/${resultsScenathonId}/participating-countries`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const codes = Array.isArray(data?.countries)
          ? data.countries.map((c) => c.country_code)
          : [];
        setParticipatingCountries(codes);
      } catch (err) {
        console.error("Error loading participating countries:", err);
        setParticipatingCountries([]);
      } finally {
        setLoadingParticipatingCountries(false);
      }
    };

    loadParticipatingCountries();
  }, [resultsScenathonId]);

  // 🌍 Cargar países con datos en indicators_scenathon y trade_scenathon
  useEffect(() => {
    if (!resultsScenathonId || !resultsPathwayId) {
      setCountriesWithData([]);
      return;
    }

    const loadCountriesWithData = async () => {
      setLoadingCountriesWithData(true);
      try {
        const params = new URLSearchParams({
          pathwayName: String(resultsPathwayId),
        });
        const res = await apiFetch(
          `${API}/api/scenathon/scenathons/${resultsScenathonId}/countries-with-data?${params.toString()}`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setCountriesWithData(Array.isArray(data?.countriesWithData) ? data.countriesWithData : []);
      } catch (err) {
        console.error("Error loading countries with data:", err);
        setCountriesWithData([]);
      } finally {
        setLoadingCountriesWithData(false);
      }
    };

    loadCountriesWithData();
  }, [resultsScenathonId, resultsPathwayId]);

  const normalized = useMemo(
    () => normalizeResultsPayload(resultsPayload),
    [resultsPayload],
  );

  const countriesFromResults = useMemo(
    () => extractCountriesFromCharts(normalized?.charts),
    [normalized],
  );

  const pathwayNode = normalized?.node || null;
  const pathwayKey = normalized?.pathwayKey || "";

  const pathwayOptions = useMemo(
    () =>
      pathways.map((p) => ({
        value: String(p.id),
        label: p.name || p.pathway_name || String(p.id),
      })),
    [pathways],
  );

  const defaultPathwayOptions = useMemo(
    () =>
      defaultPathways.map((p) => ({
        value: String(p.id),
        label: p.defaultname,
      })),
    [defaultPathways],
  );

  const availablePathwaysInResults = useMemo(() => {
    if (dashboardPathways.length > 0) {
      return dashboardPathways;
    }

    if (!resultsPayload) return [];

    try {
      const root = Array.isArray(resultsPayload) ? resultsPayload[0] : resultsPayload;
      if (!root || typeof root !== "object") return [];

      const keys = Object.keys(root).filter((key) => {
        const excluded = ["id", "error", "status", "message", "timestamp"];
        return !excluded.includes(key.toLowerCase());
      });

      return keys.map((key) => ({
        value: key,
        label: key.replace(/_/g, " ").replace(/scenathon/i, "").trim() || key,
      }));
    } catch (err) {
      console.error("Error extracting available pathways:", err);
      return [];
    }
  }, [dashboardPathways, resultsPayload]);

  const dashboardPathwayOptions =
    availablePathwaysInResults.length > 0
      ? availablePathwaysInResults
      : pathwayOptions;

  const scenathonOptions = useMemo(
    () =>
      scenathons.map((s) => ({
        value: String(s.id),
        label: s.name || s.title || `Scenathon ${s.id}`,
      })),
    [scenathons],
  );

  const selectedDefaultPathwayName = useMemo(() => {
    const found = defaultPathways.find(
      (p) => String(p.id) === String(selectedDefaultPathwayId),
    );
    return found?.defaultname || "";
  }, [defaultPathways, selectedDefaultPathwayId]);

  const activeScenathonId = selectedScenathon?.id || sendScenathonId;
  const isPathwayValid = Boolean(
    selectedPathwayId &&
      pathwayOptions.some((opt) => String(opt.value) === String(selectedPathwayId)),
  );
  const isDefaultPathwayValid = Boolean(
    selectedDefaultPathwayId &&
      defaultPathwayOptions.some(
        (opt) => String(opt.value) === String(selectedDefaultPathwayId),
      ),
  );
  const canSend = Boolean(
    activeScenathonId && isPathwayValid && isDefaultPathwayValid,
  );

  const handleSend = async () => {
    if (!canSend) return;

    setSending(true);
    try {
      const res = await apiFetch(`${API}/api/scenathon/send-pathway`, {
        method: "POST",
        body: JSON.stringify({
          scenathonId: activeScenathonId,
          pathwayId: selectedPathwayId,
          defaultPathwayId: selectedDefaultPathwayId,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const data = await res.json();

      let finalPathwayName =
        data?.pathwayName || selectedDefaultPathwayName || "";

      // Add "- adjusted" suffix if trade adjustment was yes
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        if (stored.tradeAdjustment === "yes") {
          finalPathwayName = `${finalPathwayName} - adjusted`;
        }
      } catch (e) {
        // ignore storage errors
      }

      setResultsScenathonId(String(activeScenathonId));
      setResultsPathwayId(finalPathwayName);

      await loadScenathonResults(activeScenathonId, finalPathwayName);

      Swal.fire({
        icon: "success",
        title: "Pathway sent successfully",
        text: `The pathway was sent successfully with the name "${finalPathwayName}".`,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Could not send the pathway",
        text: err.message || "An error occurred sending the pathway.",
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white px-6 py-4 text-sm text-[#6B7280] shadow-sm">
          Loading scenathons and pathways...
        </div>
      </div>
    );
  }

  return (
    <div className="mt-9">
      <main className="mx-auto py-8 px-6 min-h-screen">
        {canSeeSubmission && sectionTab === "submission" && (
          <SubmissionForm 
            scenathonId={selectedScenathon?.id || sendScenathonId}
            scenathonOptions={scenathonOptions}
            onScenathonChange={setSendScenathonId}
            apiFetch={apiFetch}
            API={API}
            defaultPathways={defaultPathways}
            loadingDefaultPathways={loadingDefaultPathways}
            selectedDefaultPathwayId={selectedDefaultPathwayId}
            onDefaultPathwayChange={setSelectedDefaultPathwayId}
            isAdmin={user?.type_id === 1}
            isTeamleader={user?.type_id === 3}
            user={user}
          />
        )}

        <div
          className={`rounded-3xl border border-slate-200 bg-white p-8 shadow-sm hover:shadow-md transition-shadow ${
            sectionTab !== "dashboard"
              ? "hidden"
              : ""
          }`}
        >
          <div className="flex flex-col gap-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-1 h-8 bg-gradient-to-b from-[#54B046] to-emerald-500 rounded-full"></div>
                  <h2 className="text-3xl font-bold text-slate-900">
                    Scenathon Dashboard
                  </h2>
                </div>
                <p className="mt-3 text-slate-600 ml-4">
                  Navigate the results of a Scenathon and compare between pathways.
                </p>
              </div>

              <div className="inline-flex w-fit rounded-2xl border border-slate-200 bg-slate-50 p-1.5">
                <button
                  type="button"
                  onClick={() => setDashboardTab("results")}
                  className={dashboardTab === "results" ? "rounded-lg px-5 py-2.5 text-sm font-semibold transition bg-white text-slate-900 shadow-md" : "rounded-lg px-5 py-2.5 text-sm font-semibold transition text-slate-600 hover:text-slate-900"}
                >
                  Results
                </button>
                <button
                  type="button"
                  onClick={() => setDashboardTab("comparison")}
                  className={dashboardTab === "comparison" ? "rounded-lg px-5 py-2.5 text-sm font-semibold transition bg-white text-slate-900 shadow-md" : "rounded-lg px-5 py-2.5 text-sm font-semibold transition text-slate-600 hover:text-slate-900"}
                >
                  Comparison
                </button>
              </div>
            </div>

            {dashboardTab === "results" ? (
              <div className="grid gap-4 lg:grid-cols-3 lg:items-end">
                {/* Admin: can choose any scenathon | Teamleader: sees own scenathon (read-only) */}
                {((user?.type_id === 1 && !selectedScenathon?.id) || user?.type_id === 3) && (
                  <SelectField
                    label="Scenathon"
                    value={resultsScenathonId}
                    onChange={setResultsScenathonId}
                    options={scenathonOptions}
                    placeholder="Select scenathon"
                    disabled={user?.type_id === 3}
                  />
                )}

                <SelectField
                  label="Pathway"
                  value={resultsPathwayId}
                  onChange={setResultsPathwayId}
                  options={dashboardPathwayOptions}
                  placeholder="Select pathway"
                />

                {pathwayNode?.date && (
                  <div>
                    <label className="text-sm font-semibold text-[#111827]">Date</label>
                    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                      {pathwayNode.date}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2 lg:items-end">
                {/* Comparison tab: always show scenathon selector for everyone */}
                <SelectField
                  label="Scenathon"
                  value={resultsScenathonId}
                  onChange={setResultsScenathonId}
                  options={scenathonOptions}
                  placeholder="Select scenathon"
                  disabled={false}
                />
              </div>
            )}
          </div>

          {/* Results tab content */}
          <div className={dashboardTab !== "results" ? "hidden" : ""}>
            {(!resultsScenathonId || !resultsPathwayId) ? (
              <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
                <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7 20h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v11a2 2 0 002 2z" />
                </svg>
                <p className="text-slate-600 font-medium">Select a scenathon and pathway to view results.</p>
              </div>
            ) : loadingResults ? (
              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-12 text-center">
                <div className="flex justify-center mb-3">
                  <svg className="animate-spin h-10 w-10 text-[#54B046]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="text-slate-600 font-medium">Loading results...</p>
              </div>
            ) : resultsError ? (
              <div className="mt-8 rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50 p-6">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  <div>
                    <p className="font-semibold text-rose-900">Error loading results</p>
                    <p className="text-rose-800 text-sm mt-1">{resultsError}</p>
                  </div>
                </div>
              </div>
            ) : !pathwayNode ? (
              <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
                <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <p className="text-slate-600 font-medium">Select a scenathon and pathway to load the charts.</p>
              </div>
            ) : (
              <div className="mt-8 space-y-6">
                
                <CountriesWithSubmissionStatus
                  participatingCountries={participatingCountries}
                  countriesWithSubmissions={countriesFromSubmissions}
                  countriesWithData={countriesWithData}
                  loading={loadingParticipatingCountries || loadingCountries || loadingCountriesWithData}
                />
                <div className="flex items-center justify-end gap-4">
                  {resultsScenathonId && resultsPathwayId && (
                    <button
                      type="button"
                      onClick={() => loadScenathonResults(resultsScenathonId, resultsPathwayId)}
                      disabled={loadingResults}
                      title="Refresh charts"
                      className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#54B046] to-emerald-500 hover:shadow-lg px-5 py-2.5 text-sm font-semibold text-white transition-all hover:scale-105 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:scale-100 disabled:shadow-none group"
                    >
                      <svg
                        className={`h-4 w-4 transition-transform ${loadingResults ? "animate-spin" : "group-hover:rotate-180"} duration-300`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      {loadingResults ? "Refreshing..." : "Update Charts"}
                    </button>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <ScenathonCharts
                    resultsData={resultsPayload}
                    scenathonId={resultsScenathonId}
                    pathwayName={resultsPathwayId}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Comparison tab — always mounted to preserve selections */}
          <div className={dashboardTab !== "comparison" ? "hidden" : "mt-8"}>
            <ComparisonScenathon
              scenathonId={resultsScenathonId}
              requireScenathon
              embedded
            />
          </div>
        </div>
      </main>
    </div>
  );
}